import * as React from 'react';
import { Component, Fragment } from 'react';
import { action, computed, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import SwipeableViews from 'react-swipeable-views';
import FundCardHeader from './FundCardHeader';
import _FundFacts from './FundFacts';
import FundCommentary from './FundCommentary';
import Charts from './Charts';
import Chart from './Chart';
import FundManagers from './FundManagers';
import KeyDocuments from './KeyDocuments';
import Disclaimers from './Disclaimers';
import withColumns from './withColumns';
import Tabs from './Tabs';
import Tab from './Tab';

const FundFacts = withColumns(_FundFacts);

const FundCardWrapper = styled<any, any>('div')`
    ${propStyles};
`;

FundCardWrapper.defaultProps = {
    bgColor: "white"
};

const FundCardColumns = styled<any, any>('div')`
    ${propStyles};
`;

FundCardColumns.defaultProps = {
    display: "flex",
    flexDirection: ["column", "row"],
    bgColor: "white"
};

const FundCardMain = styled<any, any>('div')`
    ${propStyles};
`;

FundCardMain.defaultProps = {
    flex: "0 1 100%",
    minWidth: "0",
    paddingX: ["sm", "md", "lg"],
    paddingY: ["sm", "md", "lg"]
};

const FundCardSidebar = styled<any, any>('div')`
    ${propStyles};
`;

FundCardSidebar.defaultProps = {
    width: "240px",
    flex: "0 0 auto",
    paddingX: ["md", "lg"],
    paddingY: ["md", "lg"]
};

const SwipeyTabs = styled<any, any>(Tabs)`
    position: relative;
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-behavior: smooth;
    scroll-snap-type: mandatory;
    scroll-snap-destination: center;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const SwipeyTab = styled<any, any>(Tab)`
    scroll-snap-coordinate: center;
    &:first-child {
        scroll-snap-coordinate: left;
    }
`;

// Fixes issue with use of offsetHeight in react-swipeable-views
// Adding a wrapper with a border ensures offsetHeight value includes margin
const SwipeableViewWrapper = styled<any, any>('div')`
    border: 1px solid transparent;
`;

// Swipeable view style overrides for expanded view
const expandedRootStyle = {
    overflow: "visible"
};

const expandedContainerStyle = {
    flexDirection: "column",
    height: "auto"
};

const expandedSlideStyle = {
    overflow: "visible"
};

@inject('dataStore')
@observer
class FundCard extends Component<any, any> {
    @observable selectedTabIndex = 0;

    @computed
    get header() {
        return this.props.dataStore.config.rowConfig.header || {};
    }

    @computed
    get fundCommentary() {
        return this.props.dataStore.config.rowConfig.fundCommentary || {};
    }

    @computed
    get hasFundCommentary() {
        return Object.keys(this.fundCommentary).length > 0 && this.props.shareClass.commentaries;
    }

    @computed
    get fundFacts() {
        return this.props.dataStore.config.rowConfig.fundFacts || {};
    }

    @computed
    get charts() {
        return this.props.dataStore.config.rowConfig.charts || [];
    }

    @computed
    get fundManagersConfig() {
        return this.props.dataStore.config.rowConfig.fundManagers || {};
    }

    @computed
    get fundManagers() {
        return Object.keys(this.fundManagersConfig).length > 0 ? this.props.shareClass.fundManagers : [];
    }

    @computed
    get keyDocumentsConfig() {
        return this.props.dataStore.config.rowConfig.keyDocuments || {};
    }

    @computed
    get keyDocuments() {
        const { documentTypes = [] } = this.keyDocumentsConfig;
        return documentTypes
            .map(d => this.props.groupedDocuments.find(g => g.documentGroupName === d))
            .filter(d => d !== undefined);
    }

    @computed
    get showSidebar() {
        return this.fundManagers.length > 0 || this.keyDocuments.length > 0;
    }

    @computed
    get tabs() {
        const tabs = [];

        if (this.hasFundCommentary) {
            tabs.push({
                id: "fundCommentary",
                label: this.fundCommentary.label
            });
        }

        if (Object.keys(this.fundFacts).length > 0) {
            tabs.push({
                id: "fundFacts",
                label: this.fundFacts.label
            });
        }

        if (this.charts.length > 0) {
            this.charts.map(c => {
                tabs.push({
                    id: "chart",
                    label: c.label,
                    config: c
                });
            });
        }

        if (this.fundManagers.length > 0) {
            tabs.push({
                id: "fundManagers",
                label: this.fundManagersConfig.label
            });
        }

        if (this.keyDocuments.length > 0) {
            tabs.push({
                id: "keyDocuments",
                label: this.keyDocumentsConfig.label
            });
        }

        return tabs;
    }

    @action
    setSelectedTab = (tabNode, tabIndex) => {
        if (tabNode) {
            const tabLeft = tabNode.offsetLeft;
            const tabWidth = tabNode.offsetWidth;
            const parentWidth = tabNode.parentNode.offsetWidth;
            tabNode.parentNode.scrollLeft = tabLeft - ((parentWidth - tabWidth) / 2);
        }

        this.selectedTabIndex = tabIndex;
    }

    render() {
        const {
            header,
            fundCommentary,
            hasFundCommentary,
            fundFacts,
            charts,
            fundManagersConfig,
            fundManagers,
            keyDocumentsConfig,
            keyDocuments,
            showSidebar,
            tabs,
            selectedTabIndex,
            setSelectedTab
        } = this;

        const { theme, dataStore, shareClass, fundCardDisclaimers, isMobile } = this.props;

        const getFundCardComponent = (tabId, config) => {
            if (tabId === "fundCommentary") {
                return (
                    <FundCommentary
                        showHeading={dataStore.isExpandAll}
                        fundCommentary={fundCommentary}
                        shareClass={shareClass}
                    />
                );
            }

            if (tabId === "fundFacts") {
                return (
                    <FundFacts
                        showHeading={dataStore.isExpandAll}
                        fundFacts={fundFacts}
                        shareClass={shareClass}
                    />
                );
            }

            if (tabId === "fundManagers") {
                return (
                    <FundManagers
                        showHeading={dataStore.isExpandAll}
                        fundManagersConfig={fundManagersConfig}
                        fundManagers={fundManagers}
                        isMobile={isMobile}
                    />
                );
            }

            if (tabId === "keyDocuments") {
                return (
                    <KeyDocuments
                        showHeading={dataStore.isExpandAll}
                        keyDocumentsConfig={keyDocumentsConfig}
                        keyDocuments={keyDocuments}
                        shareClass={shareClass}
                    />
                );
            }

            if (tabId === "chart") {
                return (
                    <Chart
                        showHeading={dataStore.isExpandAll}
                        chart={config}
                        shareClass={shareClass}
                        isMobile={isMobile}
                    />
                );
            }

            return null;
        };

        return (
            <FundCardWrapper
                {...theme.FundCardWrapper}
            >
                {
                    header && (
                        <FundCardHeader
                            header={header}
                            shareClass={shareClass}
                        />
                    )
                }
                {
                    isMobile
                        ? (
                            <Fragment>
                                {
                                    !dataStore.isExpandAll && (
                                        <SwipeyTabs
                                            {...theme.FundCardMobileTabs}
                                        >
                                            {
                                                tabs.map((tab, index) => (
                                                    <SwipeyTab
                                                        key={`${tab.id}-${index}`}
                                                        innerRef={(n) => selectedTabIndex === index ? setSelectedTab(n, index) : null}
                                                        onClick={(e) => setSelectedTab(e.target, index)}
                                                        isSelected={selectedTabIndex === index}
                                                        flex="0 0 auto"
                                                        {...theme.FundCardMobileTab}
                                                    >
                                                        {tab.label}
                                                    </SwipeyTab>
                                                ))
                                            }
                                        </SwipeyTabs>
                                    )
                                }
                                <FundCardMain
                                    {...theme.FundCardMobileMain}
                                >
                                    <SwipeableViews
                                        disabled={dataStore.isExpandAll}
                                        style={dataStore.isExpandAll ? expandedRootStyle : {}}
                                        containerStyle={dataStore.isExpandAll ? expandedContainerStyle : {}}
                                        slideStyle={dataStore.isExpandAll ? expandedSlideStyle : {}}
                                        enableMouseEvents={true}
                                        animateHeight={dataStore.isExpandAll ? false : true}
                                        resistance={true}
                                        index={selectedTabIndex}
                                        onChangeIndex={(index) => setSelectedTab(null, index)}
                                    >
                                        {
                                            tabs.map((tab, index) => (
                                                <SwipeableViewWrapper key={`${tab.id}-${index}`}>
                                                    {getFundCardComponent(tab.id, tab.config)}
                                                </SwipeableViewWrapper>
                                            ))
                                        }
                                    </SwipeableViews>
                                </FundCardMain>
                                <Disclaimers
                                    disclaimers={fundCardDisclaimers}
                                    marginX="sm"
                                    theme={{
                                        ...theme,
                                        DisclaimersList: theme.FundCardDisclaimersList,
                                        DisclaimerListItem: theme.FundCardDisclaimerListItem,
                                        DisclaimerCharacter: theme.FundCardDisclaimerCharacter,
                                        DisclaimerContent: theme.FundCardDisclaimerContent
                                    }}
                                />
                            </Fragment>
                        )
                        : (
                            <FundCardColumns
                                {...theme.FundCardColumns}
                            >
                                <FundCardMain
                                    {...theme.FundCardMain}
                                >
                                    {
                                        hasFundCommentary && (
                                            <FundCommentary
                                                showHeading={true}
                                                fundCommentary={fundCommentary}
                                                shareClass={shareClass}
                                            />
                                        )
                                    }
                                    {
                                        fundFacts && (
                                            <FundFacts
                                                showHeading={true}
                                                fundFacts={fundFacts}
                                                shareClass={shareClass}
                                            />
                                        )
                                    }
                                    {
                                        charts && (
                                            <Charts
                                                charts={charts}
                                                shareClass={shareClass}
                                            />
                                        )
                                    }
                                    <Disclaimers
                                        disclaimers={fundCardDisclaimers}
                                        theme={{
                                            ...theme,
                                            DisclaimersList: theme.FundCardDisclaimersList,
                                            DisclaimerListItem: theme.FundCardDisclaimerListItem,
                                            DisclaimerCharacter: theme.FundCardDisclaimerCharacter,
                                            DisclaimerContent: theme.FundCardDisclaimerContent
                                        }}
                                    />
                                </FundCardMain>
                                {
                                    showSidebar && (
                                        <FundCardSidebar
                                            {...theme.FundCardSidebar}
                                        >
                                            {
                                                (fundManagers.length > 0) &&
                                                <FundManagers
                                                    showHeading={true}
                                                    fundManagersConfig={fundManagersConfig}
                                                    fundManagers={fundManagers}
                                                />
                                            }
                                            {
                                                (keyDocuments.length > 0) &&
                                                <KeyDocuments
                                                    showHeading={true}
                                                    keyDocumentsConfig={keyDocumentsConfig}
                                                    keyDocuments={keyDocuments}
                                                    shareClass={shareClass}
                                                />
                                            }
                                        </FundCardSidebar>
                                    )
                                }
                            </FundCardColumns>
                        )
                }
            </FundCardWrapper>
        );
    }
}

export default withTheme(FundCard) as any;


// WEBPACK FOOTER //
// ./src/Components/FundCard.tsx